import axios, { AxiosRequestConfig, AxiosRequestHeaders } from "axios";
import { getEnvVar } from "../utils/get-env";
import { store } from "../redux/store";
import { clearSessionAndRedirect, isInvalidSignatureResponse, isTokenExpiredResponse, refreshTokenOnce } from "./refreshTokenOnce";

type RetryAxiosRequestConfig = AxiosRequestConfig & {
  _retry?: boolean;
};

const baseURL = process.env.NODE_ENV === "local" ? "http://localhost:3838" : getEnvVar("REACT_APP_VR_ASSISTANT_URL");

export const gatewayPhone = axios.create({
  baseURL,
  timeout: 90000,
  headers: {
    "ngrok-skip-browser-warning": "69420",
  },
});

gatewayPhone.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem("token");
    const companyId = store.getState().userState.company?.id;

    config.headers = config.headers || ({} as AxiosRequestHeaders);

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (companyId && !config.headers["x-company-id"]) {
      config.headers["x-company-id"] = String(companyId);
    }

    const pushEnv = String(process.env.REACT_APP_TWILIO_PUSH_ENV ?? "")
      .trim()
      .toLowerCase();
    if (pushEnv === "sandbox" || pushEnv === "production") {
      config.headers["x-twilio-push-env"] = pushEnv;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

gatewayPhone.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!error.response) {
      return Promise.reject(error);
    }

    const originalRequest = error.config as RetryAxiosRequestConfig;

    originalRequest.headers = originalRequest.headers || {};

    const status = error.response.status;

    const hasStoredTokens = Boolean(sessionStorage.getItem("token")) && Boolean(sessionStorage.getItem("refresh_token"));

    const isRefreshRequest = typeof originalRequest?.url === "string" && originalRequest.url.includes("/tokens/refresh");

    if (status === 401 && isInvalidSignatureResponse(error)) {
      clearSessionAndRedirect();
      return Promise.reject(error);
    }

    if (status === 401 && hasStoredTokens && !originalRequest._retry && !isRefreshRequest && isTokenExpiredResponse(error)) {
      originalRequest._retry = true;

      try {
        const newToken = await refreshTokenOnce();

        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        const nextCompanyId = store.getState().userState.company?.id;

        if (nextCompanyId) {
          originalRequest.headers["x-company-id"] = String(nextCompanyId);
        }

        return gatewayPhone(originalRequest);
      } catch (refreshError) {
        clearSessionAndRedirect();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);
